import { useStore } from '@nanostores/react';
import { FitAddon } from '@xterm/addon-fit';
import { WebLinksAddon } from '@xterm/addon-web-links';
import { Terminal as XTerm, type ITheme } from '@xterm/xterm';
import { forwardRef, memo, useEffect, useImperativeHandle, useRef, type ComponentProps } from 'react';
import { getTerminalTheme } from './theme';
import { themeStore } from '~/lib/stores/theme';
import { workbenchStore } from '~/lib/stores/workbench';
import { createScopedLogger } from '~/utils/logger';

const logger = createScopedLogger('Terminal');

export interface TerminalProps extends ComponentProps<'div'> {
  className?: string;
  readonly?: boolean;
  children?: string;
  theme?: ITheme;

  // init-only prop
  webLinks?: boolean;
}

export interface TerminalRef {
  instance: XTerm;
}

export const Terminal = memo(
  forwardRef<TerminalRef, TerminalProps>(({ className, theme, children, readonly, webLinks = true, ...props }, ref) => {
    const parentRef = useRef<HTMLDivElement>(null);
    const terminalRef = useRef<XTerm>();
    const colorTheme = useStore(themeStore);

    useEffect(() => {
      const parent = parentRef.current!;

      const { terminal, dispose } = createXTerm({
        parent,
        readonly,
        webLinks,
      });

      terminalRef.current = terminal;

      logger.debug('Attach terminal');

      return dispose;
    }, []);

    useEffect(() => {
      const terminal = terminalRef.current;

      if (!terminal) {
        return;
      }

      /**
       * We use `requestAnimationFrame` to ensure proper timing. This hook triggers
       * before the shared layout component's hook, which updates the theme attribute
       * on the html element. Without requestAnimationFrame, the CSS variables wouldn't
       * be updated yet, as the attribute change hasn't been processed.
       *
       * By using `requestAnimationFrame`, we allow the browser to complete the attribute
       * update and recalculate styles before applying the terminal theme, ensuring that
       * all necessary CSS variables are available and correctly set when we update the
       * terminal's appearance.
       */
      requestAnimationFrame(() => {
        terminal.options.theme = getTerminalTheme({
          // we render a transparent cursor in case the terminal is readonly
          ...(readonly && { cursor: '#00000000' }),
          ...theme,
        });
      });

      terminal.options.disableStdin = readonly;
    }, [colorTheme, theme, readonly]);

    useEffect(() => {
      const terminal = terminalRef.current;

      if (terminal && typeof children === 'string') {
        terminal.clear();
        terminal.write(children);
      }
    }, [children]);

    useImperativeHandle(ref, () => {
      return {
        get instance() {
          return terminalRef.current!;
        },
      };
    }, []);

    return <div translate="no" className={className} ref={parentRef} {...props} />;
  }),
);

function createXTerm({ readonly, webLinks, parent }: { readonly?: boolean; webLinks?: boolean; parent: HTMLElement }) {
  const fitAddon = new FitAddon();

  const terminal = new XTerm({
    cursorBlink: true,
    convertEol: true,
    disableStdin: readonly,
    theme: getTerminalTheme(readonly ? { cursor: '#00000000' } : {}),
    fontSize: 12,
    fontFamily: 'Menlo, courier-new, courier, monospace',
  });

  terminal.loadAddon(fitAddon);

  if (webLinks) {
    terminal.loadAddon(new WebLinksAddon());
  }

  terminal.open(parent);

  /**
   * Fix for scroll events firing just before terminal is rendered
   * because of the 0px height of the resizable container.
   */
  const unsubscribeShowTerminal = workbenchStore.showTerminal.listen(() => {
    requestAnimationFrame(() => {
      terminal.scrollToBottom();
    });
  });

  const resizeObserver = new ResizeObserver(() => {
    fitAddon.fit();
  });

  resizeObserver.observe(parent);

  return {
    terminal,
    dispose: () => {
      resizeObserver.disconnect();
      terminal.dispose();
      unsubscribeShowTerminal();
    },
  };
}
